import React from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, Link } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import { singleSelectDropdownStyles } from "components/ReportComponents/singleSelectDropdownStyles";
import { FormButton } from "components/FormComponents/Button/FormButton";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { activityStyles } from "../activityStyles";
import { store, useAppSelector } from "../../../../redux/store";
import {
  initialMessage,
  isValidObject,
  loggedMessage,
  message,
  scrollToTop,
  theme,
} from "commonUtils/utils";
import { postRequest, setHeader } from "repository/genericRepository";
import {
  ActivityBasicSearchForm,
  ActivityDescriptions,
  AdvanceSearchBrands,
  AdvanceSearchState,
  handleAxiosErrors,
} from "../activityUtils";
import { CPSConstants, PageNames } from "commonUtils/enumerations";
import {
  ACTIVITY_BASIC_SEARCH_CONTROL,
  PID_SEARCH,
} from "commonUtils/globalAPISettings";
import { Autocomplete, MenuItem } from "@mui/material";
import { adminStyles } from "features/Administration/components/adminStyles";

const SearchActivities = () => {
  const navigate = useNavigate();

  const [loader, setLoader] = React.useState<boolean>(false);
  const [servervalidation, setServerValidation] =
    React.useState<message>(initialMessage);
  const [showAlert, setShowAlert] = React.useState<boolean>(false);
  const [errorMsg, setErrorMsg] = React.useState<string>("");
  const [activityDescriptionsList, setActivityDescriptionsList] =
    React.useState<ActivityDescriptions[]>([]);
  const [brandsList, setBrandsList] = React.useState<AdvanceSearchBrands[]>([]);
  const [stateList, setStateList] = React.useState<AdvanceSearchState[]>([]);
  const [pidNumberList, setPIDNumberList] = React.useState<string[]>([]);
  const [open, setOpen] = React.useState(false);
  const [isBrandsVisible, setIsBrandsVisible] = React.useState(false);

  const closeAutoCompleteSelector = () => setOpen(false);
  const openAutoCompleteSelector = () => setOpen(true);

  const PLEASE_SELECT = -1;

  const defaultFormValue = {
    activityNumber: "",
    pidNumber: null,
    brandId: PLEASE_SELECT,
    startDate: null,
    endDate: null,
    activityDescriptionId: PLEASE_SELECT,
    stateId: PLEASE_SELECT,
    supplierName: "",
  };

  const { handleSubmit, control, getValues, setValue } = useForm<any>({
    defaultValues: defaultFormValue,
  });

  React.useEffect(() => {
    scrollToTop(0, "smooth");
    fetchActivityBasicSearchControls();
  }, []);

  const { supplierId } = useAppSelector(state => {
    return state?.auth?.userDetails;
  });

  const fetchActivityBasicSearchControls = React.useMemo(
    () => async () => {
      try {
        setLoader(true);
        const authState = store.getState();

        const APIHeaders = {
          Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
        };

        const responseData = await postRequest({
          url: ACTIVITY_BASIC_SEARCH_CONTROL,
          headers: setHeader(APIHeaders),
          params: {
            supplierId,
          },
        });
        setLoader(false);
        if (isValidObject(responseData)) {
          setActivityDescriptionsList(responseData?.activityDescriptions);
          setBrandsList(responseData?.brands);
          setStateList(responseData?.states);
          setIsBrandsVisible(responseData?.isBrandsVisible);
        }
      } catch (error: any) {
        handleAxiosErrors(
          error,
          setErrorMsg,
          setShowAlert,
          setServerValidation,
        );
        loggedMessage(error, errorMsg, PageNames.ACTIVITY);
        setLoader(false);
      }
    },
    [],
  );

  const searchPIDs = React.useMemo(
    () => async (value: string) => {
      try {
        const authState = store.getState();

        const APIHeaders = {
          Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
        };

        const responseData = await postRequest({
          url: PID_SEARCH,
          headers: setHeader(APIHeaders),
          params: {
            pid: value,
          },
        });

        if (isValidObject(responseData)) {
          setPIDNumberList(responseData?.piDs);
          if (responseData?.piDs?.length > 0) {
            openAutoCompleteSelector();
          }
        }
      } catch (error: any) {
        handleAxiosErrors(
          error,
          setErrorMsg,
          setShowAlert,
          setServerValidation,
        );
        loggedMessage(error, errorMsg, PageNames.ACTIVITY);
      }
    },
    [],
  );

  const searchActivityResult = (data: ActivityBasicSearchForm) => {
    navigate("/activity/searchactivityresult", {
      state: { searchParams: { ...data, isBasicSearch: true } },
    });
  };

  const handleOnDateBlur = (key: string) => {
    let value = getValues(`${key}`);

    if (value?.$d && !value?.isValid()) {
      setValue(key, null);
    }
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          handleSubmit(searchActivityResult)(event);
        }}
        noValidate
        sx={{
          ...activityStyles.activitySearchContainer,
          ...activityStyles.activityBoxStyle,
        }}>
        <Box sx={activityStyles.activityBasicBoxContainer}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                Activity Number
              </InputLabel>
              <Controller
                name="activityNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="text"
                    size="small"
                    className="input-font-family"
                    fullWidth
                    {...field}
                    inputProps={{
                      maxLength: CPSConstants.MAX_LENGTH_TWENTY_NINE,
                    }}
                    sx={activityStyles.activityTextField}></TextField>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                PID Number
              </InputLabel>
              <Controller
                key={"pidNumber"}
                name={"pidNumber"}
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <ThemeProvider theme={theme}>
                    <Autocomplete
                      freeSolo
                      options={pidNumberList}
                      getOptionLabel={(option: any) => option}
                      popupIcon={null}
                      sx={adminStyles.defaultFontFamily}
                      disableClearable={false}
                      {...field}
                      value={field.value}
                      onChange={(event, newValue) => {
                        field.onChange(newValue);
                        closeAutoCompleteSelector();
                      }}
                      open={open}
                      onClose={closeAutoCompleteSelector}
                      renderInput={params => (
                        <TextField
                          {...params}
                          sx={activityStyles.activityTextField}
                          className="input-font-family"
                          onChange={e => {
                            let { value } = e?.target;
                            field.onChange(value);
                            if (value?.length > 3) {
                              searchPIDs(value);
                            }
                          }}
                          onBlur={e => {
                            setTimeout(() => {
                              setPIDNumberList([]);
                            }, 300);
                          }}
                          inputProps={{
                            ...params.inputProps,
                            maxLength: CPSConstants.MAX_LENGTH_FIFTY,
                            style: adminStyles.defaultFontFamily,
                          }}
                          type="text"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    />
                  </ThemeProvider>
                )}
              />
            </Grid>
            {isBrandsVisible && (
              <Grid item xs={12}>
                <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                  Brand/Disease State
                </InputLabel>
                <Controller
                  name="brandId"
                  control={control}
                  render={({ field }) => (
                    <ThemeProvider theme={theme}>
                      <Select
                        size="small"
                        className="search-basic-select-box"
                        {...field}
                        sx={activityStyles.activityTextField}>
                        {brandsList?.map(
                          (item: AdvanceSearchBrands, index: number) => (
                            <MenuItem
                              key={index}
                              value={item.id}
                              title={item.brandName}>
                              {item.brandName}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                    </ThemeProvider>
                  )}
                />
              </Grid>
            )}
            <Grid
              item
              xs={12}
              sx={{
                ...activityStyles.datePckerPaddingIcon,
              }}
              className="custom-datepicker">
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                Start Date
              </InputLabel>
              <Controller
                name={`startDate`}
                control={control}
                render={({ field }) => (
                  <ThemeProvider theme={theme}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        {...field}
                        onChange={(newValue: any) => {
                          field.onChange(newValue);
                        }}
                        renderInput={(params: any) => (
                          <TextField
                            className="input-font-family"
                            size={"small"}
                            sx={activityStyles.activityTextField}
                            {...params}
                            onBlur={e => handleOnDateBlur("startDate")}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </ThemeProvider>
                )}
              />
            </Grid>
            <Grid
              item
              xs={12}
              className="custom-datepicker"
              sx={{
                ...activityStyles.datePckerPaddingIcon,
              }}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                End Date
              </InputLabel>
              <Controller
                name={`endDate`}
                control={control}
                render={({ field }) => (
                  <ThemeProvider theme={theme}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        {...field}
                        onChange={(newValue: any) => {
                          field.onChange(newValue);
                        }}
                        renderInput={(params: any) => (
                          <TextField
                            className="input-font-family"
                            size={"small"}
                            sx={activityStyles.activityTextField}
                            {...params}
                            onBlur={e => handleOnDateBlur("endDate")}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </ThemeProvider>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                Activity Description
              </InputLabel>
              <Controller
                name="activityDescriptionId"
                control={control}
                render={({ field }) => (
                  <ThemeProvider theme={theme}>
                    <Select
                      size="small"
                      className="search-basic-select-box"
                      {...field}
                      sx={activityStyles.activityTextField}>
                      {activityDescriptionsList?.map(
                        (item: ActivityDescriptions, index: number) => (
                          <MenuItem
                            key={index}
                            value={item.id}
                            title={item.name}>
                            {item.name}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </ThemeProvider>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                State
              </InputLabel>
              <Controller
                name="stateId"
                control={control}
                render={({ field }) => (
                  <ThemeProvider theme={theme}>
                    <Select
                      size="small"
                      className="search-basic-select-box"
                      {...field}
                      sx={activityStyles.activityTextField}>
                      {stateList?.map(
                        (item: AdvanceSearchState, index: number) => (
                          <MenuItem
                            key={index}
                            value={item.id}
                            title={item.name}>
                            {item.name}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </ThemeProvider>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                Supplier Name
              </InputLabel>
              <Controller
                name="supplierName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    size="small"
                    className="input-font-family"
                    fullWidth
                    inputProps={{
                      maxLength: CPSConstants.MAX_LENGTH_HUNDRED,
                    }}
                    sx={activityStyles.activityTextField}></TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} mt={1}>
              <FormButton
                variant="contained"
                size="small"
                type="submit"
                outlet="Search Activities"
              />
              <Link to="/activity/advancesearchactivity">
                <Box sx={activityStyles.advanceSearchLink}>
                  <KeyboardDoubleArrowRightIcon />
                  Advanced Search
                </Box>
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default SearchActivities;
